import {useEffect, type RefObject} from 'react'
import type {StylingDirectivesLine} from '../types'
import {parseDirectives} from '../ast-utils/parse-node'
import {addHighlight} from '../utils/highlight'
import {createRange} from '../utils/range'

/**
 * Custom hook to apply syntax highlighting to a target element based on provided style directives.
 * This hook uses the CSS Custom Highlighting API to apply styles to specific ranges within the target element.
 * It expects the target to be a ref to an HTMLElement. It will apply the styles to either the ref itself
 * or a child element matching the provided class selector.
 * @param target the ref to the target HTMLElement to apply highlighting to
 * @param styleDirectives the style directives to apply for highlighting
 * @param targetClassName optional class name to target a specific child element for highlighting
 */
export function useHighlight(
  target: RefObject<HTMLElement>,
  styleDirectives: StylingDirectivesLine | undefined,
  targetClassName?: string,
) {
  useEffect(() => {
    // Early exit if no directives
    if (!styleDirectives || styleDirectives.length === 0) return

    // Early exit if target is not defined
    if (!target || !target.current) return

    // Determine the target element - either the ref or a child element
    let targetElement: HTMLElement | null = null

    // If the target is a ref, check if it points to an HTMLElement and not a ref callback function
    if (target.current instanceof HTMLElement) {
      if (!targetClassName || target.current.classList.contains(targetClassName)) {
        // If the target is a ref and already points to the correct element
        targetElement = target.current
      } else {
        // Otherwise, find the first child with the targeted selector
        targetElement = (target.current.querySelector(`.${targetClassName}`) as HTMLDivElement) || null
      }
    }

    if (!targetElement) return

    // Apply highlighting to the target element
    const normalizedDirectives = parseDirectives(styleDirectives ?? [])

    for (const directive of normalizedDirectives) {
      const range = createRange(targetElement, directive.s, directive.e)

      if (!range) continue

      addHighlight(directive, range)
    }
  }, [target, styleDirectives, targetClassName])
}
